import type { Issue } from '../../../../api/DigiobcanApi';
import { getReportStatusMeta } from '../../../../components/AdminReportStatusItem';
import { __, formatDateTime } from '../../../../utils';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Stack,
  StatusChip,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  issues: Issue[];
  onSelect: (issueId: string) => void;
};

/**
 * Zoznam nálezov podnetu - **len mobilné rozloženie**. Riadok vedie na detail
 * nálezu ako na samostatnú obrazovku.
 *
 * Na desktope zoznam nie je: nálezy tam stoja ako karty pod sebou
 * ([IssueDetail](IssueDetail.tsx) v karte), takže ich netreba prepínať. Kým to
 * bol dvojstĺpec zoznam | detail, mal tento komponent aj desktopovú podobu
 * s vnútorným tieňom namiesto deliacej čiary; s kartami stratila zmysel.
 */
const IssueList: React.FC<Props> = ({ issues, onSelect }) => (
  <Stack sx={{ minHeight: 0 }}>
    <Stack
      direction='row'
      alignItems='baseline'
      gap={1}
      sx={{ px: 1.75, pt: 1.75, pb: 1 }}
    >
      <Typography
        variant='overline'
        color='text.secondary'
        sx={{ fontWeight: 700, letterSpacing: '.05em' }}
      >
        {__('Nálezy')}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {issues.length === 1
          ? __('1 kategória')
          : `${issues.length} ${issues.length >= 2 && issues.length <= 4 ? __('kategórie') : __('kategórií')}`}
      </Typography>
    </Stack>

    <Stack sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {issues.map((issue) => {
        const meta = getReportStatusMeta(issue.status);

        return (
          <Stack
            key={issue.id}
            direction='row'
            alignItems='center'
            gap={1}
            onClick={() => onSelect(issue.id)}
            sx={{
              cursor: 'pointer',
              flex: 'none',
              px: 1.75,
              py: 1.5,
              minHeight: 60,
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack gap={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant='body2' noWrap>
                {issue.category_name}
              </Typography>

              <Stack direction='row' alignItems='center' gap={1}>
                <StatusChip tone={meta.tone}>{meta.title}</StatusChip>
                {/* Že je nález duplikát, nesie jeho stav - druhá značka
                    v tom istom riadku by to len zdvojila. */}
                <Typography variant='caption' color='text.secondary'>
                  {formatDateTime(new Date(issue.inserted_at))}
                </Typography>
              </Stack>
            </Stack>

            <Box sx={{ color: 'text.disabled', display: 'flex' }}>
              <ChevronRightIcon />
            </Box>
          </Stack>
        );
      })}
    </Stack>
  </Stack>
);

export default IssueList;
